import { authFetch } from "./api.js";
import { registerViewLoader } from "./navigation.js";
import { formatNumber, renderPagination, showToast } from "./utils.js";
import { formatBeijingDateKey, formatBeijingDateKeyDaysAgo } from "../time-beijing.js";

const PAGE_SIZE = 10;

function getDefaultRange() {
  return {
    startDate: formatBeijingDateKeyDaysAgo(6),
    endDate: formatBeijingDateKey(),
  };
}

function ensureFilters() {
  const startInput = document.getElementById("member-consumption-start");
  const endInput = document.getElementById("member-consumption-end");
  if (startInput?.value && endInput?.value) return;
  const defaults = getDefaultRange();
  if (startInput && !startInput.value) startInput.value = defaults.startDate;
  if (endInput && !endInput.value) endInput.value = defaults.endDate;
}

function getFilters() {
  ensureFilters();
  return {
    startDate: document.getElementById("member-consumption-start")?.value || getDefaultRange().startDate,
    endDate: document.getElementById("member-consumption-end")?.value || getDefaultRange().endDate,
    search: document.getElementById("search-member-consumption")?.value?.trim() || "",
  };
}

function createMemberConsumptionElement(tagName, className = "", text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function replaceMemberConsumptionChildren(root, ...children) {
  if (!root) return;
  root.replaceChildren(...children.filter(Boolean));
}

function createEmptyState(message, icon = "") {
  const state = createMemberConsumptionElement("div", "empty-state");
  if (icon) state.append(createMemberConsumptionElement("div", "empty-icon", icon));
  state.append(createMemberConsumptionElement("p", "", message));
  return state;
}

function formatDateLabel(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-");
  const label = createMemberConsumptionElement("span", "member-consumption-day-label");
  label.append(
    createMemberConsumptionElement("span", "day-year", `${year}-${month}`),
    createMemberConsumptionElement("span", "day-date", day || ""),
  );
  return label;
}

function renderValueCell(value) {
  return Number(value)
    ? document.createTextNode(formatNumber(value))
    : createMemberConsumptionElement("span", "member-consumption-muted", "0");
}

function renderMatrixCell(className, content) {
  const cell = createMemberConsumptionElement("div", `member-consumption-cell ${className}`);
  if (content instanceof Node) cell.append(content);
  else cell.textContent = String(content ?? "");
  return cell;
}

function createMatrixRow(className = "") {
  return createMemberConsumptionElement("div", `member-consumption-grid-row${className ? ` ${className}` : ""}`);
}

function appendMatrixRow(rowElement, columns, row) {
  columns.forEach((column) => {
    rowElement.append(renderMatrixCell(column.className, column.render(row)));
  });
}

function renderMatrix({
  leftColumns = [],
  rows = [],
  dateKeys = [],
  rightColumn = null,
  leftRowClass = "",
}) {
  const rowClassSuffix = leftRowClass ? ` ${leftRowClass}` : "";
  const matrix = createMemberConsumptionElement(
    "div",
    `member-consumption-matrix${rightColumn ? " member-consumption-matrix-with-right" : " member-consumption-matrix-detail"}`,
  );
  const leftPane = createMemberConsumptionElement("div", "member-consumption-pane member-consumption-pane-left");
  const leftHead = createMatrixRow(`member-consumption-grid-row-head${rowClassSuffix}`);
  leftColumns.forEach((column) => leftHead.append(renderMatrixCell(column.className, column.header)));
  leftPane.append(leftHead);
  rows.forEach((row) => {
    const rowElement = createMatrixRow(rowClassSuffix.trim());
    appendMatrixRow(rowElement, leftColumns, row);
    leftPane.append(rowElement);
  });

  const centerPane = createMemberConsumptionElement("div", "member-consumption-pane member-consumption-pane-center");
  const scroll = createMemberConsumptionElement("div", "member-consumption-scroll");
  const track = createMemberConsumptionElement("div", "member-consumption-scroll-track");
  const centerHead = createMatrixRow("member-consumption-grid-row-head");
  dateKeys.forEach((dateKey) => centerHead.append(renderMatrixCell("member-consumption-col-day", formatDateLabel(dateKey))));
  track.append(centerHead);
  rows.forEach((row) => {
    const rowElement = createMatrixRow();
    dateKeys.forEach((dateKey, index) => {
      const value = row.dailyValues?.[index] ?? row.dailyMap?.[dateKey] ?? 0;
      rowElement.append(renderMatrixCell("member-consumption-col-day", renderValueCell(value)));
    });
    track.append(rowElement);
  });
  scroll.append(track);
  centerPane.append(scroll);
  matrix.append(leftPane, centerPane);

  if (rightColumn) {
    const rightPane = createMemberConsumptionElement("div", "member-consumption-pane member-consumption-pane-right");
    const rightHead = createMatrixRow("member-consumption-grid-row-head");
    rightHead.append(renderMatrixCell(rightColumn.className, rightColumn.header));
    rightPane.append(rightHead);
    rows.forEach((row) => {
      const rowElement = createMatrixRow();
      rowElement.append(renderMatrixCell(rightColumn.className, rightColumn.render(row)));
      rightPane.append(rowElement);
    });
    matrix.append(rightPane);
  }

  return matrix;
}

function renderLoading(message = "加载中...") {
  const panel = document.getElementById("member-consumption-table-panel");
  if (!panel) return;
  replaceMemberConsumptionChildren(panel, createEmptyState(message));
}

function renderEmpty(message = "暂无成员消耗数据") {
  const panel = document.getElementById("member-consumption-table-panel");
  if (!panel) return;
  replaceMemberConsumptionChildren(panel, createEmptyState(message, "🧮"));
}

function renderMemberConsumptionTable(payload) {
  const panel = document.getElementById("member-consumption-table-panel");
  if (!panel) return;
  const { members = [], dateKeys = [] } = payload || {};
  if (!members.length) {
    renderEmpty();
    return;
  }

  const leftColumns = [
    {
      className: "member-consumption-col-name",
      header: "成员名称",
      render: (member) => {
        const name = createMemberConsumptionElement("div", "member-name");
        const email = member.email || "";
        name.append(
          createMemberConsumptionElement("strong", "", member.name || "未命名成员"),
          createMemberConsumptionElement("span", "", email || "—"),
        );
        name.lastElementChild.title = email;
        return name;
      },
    },
    {
      className: "member-consumption-col-credits",
      header: "周期总消耗算力",
      render: (member) => formatNumber(member.totalCredits || 0),
    },
    {
      className: "member-consumption-col-image-credits",
      header: "图片算力",
      render: (member) => formatNumber(member.imageCredits || 0),
    },
    {
      className: "member-consumption-col-video-credits",
      header: "视频算力",
      render: (member) => formatNumber(member.videoCredits || 0),
    },
    {
      className: "member-consumption-col-generated",
      header: "生成总数量",
      render: (member) => formatNumber(member.generatedCount || 0),
    },
    {
      className: "member-consumption-col-downloads",
      header: "图片下载次数",
      render: (member) => formatNumber(member.totalDownloads || 0),
    },
    {
      className: "member-consumption-col-rate",
      header: "图片下载率",
      render: (member) => createMemberConsumptionElement("span", "member-consumption-rate", `${formatNumber(member.downloadRate || 0)}%`),
    },
  ];
  const rightColumn = {
    className: "member-consumption-col-action",
    header: "操作",
    render: (member) => {
      const button = createMemberConsumptionElement("button", "member-consumption-detail-btn", "查看详情");
      button.type = "button";
      button.dataset.memberConsumptionDetail = String(member.userId ?? "");
      return button;
    },
  };

  const tableShell = createMemberConsumptionElement("div", "member-consumption-table-shell");
  tableShell.append(renderMatrix({ leftColumns, rows: members, dateKeys, rightColumn, leftRowClass: "member-consumption-grid-row-member" }));
  replaceMemberConsumptionChildren(panel, tableShell);
}

function renderSummaryCards(totals = {}) {
  const root = document.getElementById("member-consumption-summary-grid");
  if (!root) return;
  const cards = [
    ["周期总消耗算力", formatNumber(totals.totalCredits || 0)],
    ["图片算力", formatNumber(totals.imageCredits || 0)],
    ["视频算力", formatNumber(totals.videoCredits || 0)],
    ["生成总数量", formatNumber(totals.generatedCount || 0)],
    ["图片下载次数", formatNumber(totals.totalDownloads || 0)],
    ["图片下载率", `${formatNumber(totals.downloadRate || 0)}%`],
  ].map(([label, value]) => {
    const card = createMemberConsumptionElement("div", "member-consumption-summary-card");
    card.append(
      createMemberConsumptionElement("span", "", label),
      createMemberConsumptionElement("strong", "", value),
    );
    return card;
  });
  replaceMemberConsumptionChildren(root, ...cards);
}

function renderDetailTable(payload) {
  const panel = document.getElementById("member-consumption-detail-panel");
  if (!panel) return;
  const { rows = [], dateKeys = [] } = payload || {};
  if (!rows.length) {
    replaceMemberConsumptionChildren(panel, createEmptyState("当前区间内暂无明细数据。"));
    return;
  }
  const leftColumns = [
    {
      className: "member-consumption-col-name",
      header: "使用功能",
      render: (row) => row.featureName || "未分类",
    },
    {
      className: "member-consumption-col-media-type",
      header: "媒体类型",
      render: (row) => row.mediaType || "图片",
    },
    {
      className: "member-consumption-col-credits",
      header: "消耗算力",
      render: (row) => formatNumber(row.totalCredits || 0),
    },
    {
      className: "member-consumption-col-generated",
      header: "生成数量",
      render: (row) => formatNumber(row.generatedCount || 0),
    },
    {
      className: "member-consumption-col-downloads",
      header: "图片下载次数",
      render: (row) => formatNumber(row.totalDownloads || 0),
    },
    {
      className: "member-consumption-col-rate",
      header: "图片下载率",
      render: (row) => createMemberConsumptionElement("span", "member-consumption-rate", `${formatNumber(row.downloadRate || 0)}%`),
    },
  ];

  const tableShell = createMemberConsumptionElement("div", "member-consumption-table-shell");
  tableShell.append(renderMatrix({ leftColumns, rows, dateKeys }));
  replaceMemberConsumptionChildren(panel, tableShell);
}

function closeMemberConsumptionDrawer() {
  document.getElementById("member-consumption-drawer-mask")?.classList.remove("open");
  document.getElementById("member-consumption-drawer")?.classList.remove("open");
  document.getElementById("member-consumption-drawer")?.setAttribute("aria-hidden", "true");
}

async function openMemberConsumptionDetail(userId) {
  const filters = getFilters();
  const params = new URLSearchParams(filters);
  const drawer = document.getElementById("member-consumption-drawer");
  const mask = document.getElementById("member-consumption-drawer-mask");
  const panel = document.getElementById("member-consumption-detail-panel");
  if (!drawer || !mask || !panel) return;

  drawer.classList.add("open");
  drawer.setAttribute("aria-hidden", "false");
  mask.classList.add("open");
  replaceMemberConsumptionChildren(panel, createEmptyState("加载详情中..."));

  try {
    const response = await authFetch(`/api/admin/member-consumption/${encodeURIComponent(userId)}?${params.toString()}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    document.getElementById("member-consumption-drawer-title").textContent = `${payload.member?.name || "成员"} 消耗详情`;
    document.getElementById("member-consumption-drawer-subtitle").textContent = `${payload.startDate} 至 ${payload.endDate} · ${payload.member?.email || "未填写邮箱"}`;
    renderSummaryCards(payload.totals);
    renderDetailTable(payload);
  } catch (error) {
    console.error("[member-consumption] detail failed:", error);
    replaceMemberConsumptionChildren(panel, createEmptyState("加载详情失败", "⚠️"));
    showToast("加载成员消耗详情失败", "error");
  }
}

export async function loadMemberConsumption(page = 1) {
  const filters = getFilters();
  const hint = document.getElementById("member-consumption-range-hint");
  if (hint) hint.textContent = `统计范围：${filters.startDate} ~ ${filters.endDate}`;
  renderLoading();
  try {
    const params = new URLSearchParams({
      ...filters,
      page: String(page),
      size: String(PAGE_SIZE),
    });
    const response = await authFetch(`/api/admin/member-consumption?${params.toString()}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    renderMemberConsumptionTable(payload);
    renderPagination("member-consumption-pagination", payload.total || 0, payload.page || 1, payload.size || PAGE_SIZE, (nextPage) => {
      loadMemberConsumption(nextPage);
    });
  } catch (error) {
    console.error("[member-consumption] load failed:", error);
    renderEmpty("加载成员消耗失败");
    showToast("加载成员消耗失败", "error");
  }
}

export function initMemberConsumption() {
  registerViewLoader("admin-member-consumption", () => {
    ensureFilters();
    loadMemberConsumption(1);
  });

  document.getElementById("filter-member-consumption-btn")?.addEventListener("click", () => loadMemberConsumption(1));
  document.getElementById("refresh-member-consumption-btn")?.addEventListener("click", () => loadMemberConsumption(1));
  document.getElementById("search-member-consumption")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") loadMemberConsumption(1);
  });
  document.getElementById("member-consumption-drawer-close")?.addEventListener("click", closeMemberConsumptionDrawer);
  document.getElementById("member-consumption-drawer-mask")?.addEventListener("click", closeMemberConsumptionDrawer);
  document.getElementById("member-consumption-table-panel")?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-member-consumption-detail]");
    if (!button) return;
    openMemberConsumptionDetail(button.dataset.memberConsumptionDetail);
  });
}
